<template>
  <div class="ibox float-e-margins">
    <div class="ibox-title">
      <h5>
        Notatki
        <small v-if="discount" class="discount-notes-subject">
          {{ discount.title }}
        </small>
      </h5>
      <div class="ibox-tools">
        <button
          v-if="discount && canAdd && notes.length < maxNotes"
          @click="openNoteModal()"
          class="btn btn-sm btn-primary"
        >
          <font-awesome-icon icon="note-sticky" class="table-helper-item" />
          <span>Dodaj notatkę</span>
        </button>
      </div>
    </div>

    <div class="ibox-content panel">
      <div class="panel-body">
        <p v-if="!discount">Wybierz rabat w drzewie, aby zobaczyć notatki.</p>
        <p v-else-if="!notes.length">Brak notatek.</p>

        <ul v-else class="sortable-list agile-list">
          <li
            v-for="note in notes"
            :key="note.pk"
            :class="canChange ? 'success-element' : 'info-element'"
          >
            <div class="clearfix">
              <div class="float-end">
                <button
                  v-if="canChange"
                  @click="openNoteModal(note)"
                  class="btn btn-xs btn-white"
                >
                  <font-awesome-icon icon="pencil" />
                  <span> Edytuj</span>
                </button>
                <button
                  v-if="canDelete"
                  @click="noteToDelete = note"
                  class="btn btn-xs btn-white"
                >
                  <font-awesome-icon icon="trash" />
                  <span> Usuń</span>
                </button>
              </div>

              <strong>
                <font-awesome-icon icon="user" />
                {{ note.created_by ?? "-" }}
              </strong>
            </div>

            <div class="m-t-sm discount-note-text" v-html="note.text"></div>

            <div class="agile-detail">
              <font-awesome-icon icon="clock" />
              Utworzono: {{ note.date_added }}
              <span v-if="isEdited(note)">
                | Edytowano: {{ note.date_modified }}
                <span v-if="note.last_modified_by">
                  <font-awesome-icon icon="user" />
                  {{ note.last_modified_by }}
                </span>
              </span>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>

  <div v-if="noteToDelete" class="modal-dialog">
    <div class="modal-content">
      <div class="inmodal">
        <div class="modal-header">
          <h4 class="modal-title">Potwierdzenie usunięcia</h4>
        </div>
        <div class="modal-body">
          <div class="form-horizontal">
            <div class="ibox-content">
              <div class="panel-body">Czy na pewno chcesz usunąć notatkę?</div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="noteToDelete = null" class="btn btn-white">
            Nie
          </button>
          <button @click="deleteNote" :disabled="saving" class="btn btn-danger">
            Tak
          </button>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="noteToDelete"
    @click="noteToDelete = null"
    class="modal-backdrop"
  ></div>

  <div v-if="editedNote" class="modal-dialog">
    <div class="modal-content">
      <div class="inmodal">
        <div class="modal-header">
          <h4 class="modal-title">
            {{
              editedNote.pk ? "Edycja notatki rabatu" : "Dodanie notatki rabatu"
            }}
          </h4>
        </div>
        <div class="modal-body">
          <div class="form-horizontal">
            <div class="ibox-content">
              <div class="panel-body">
                <div class="form-group">
                  <label class="control-label col-sm-2">Treść</label>
                  <div class="col-sm-10 QuillEditor-box">
                    <QuillEditor
                      theme="snow"
                      contentType="html"
                      :toolbar="toolbar"
                      v-model:content="editedNote.text"
                    />
                    <div
                      class="discount-note-counter"
                      :class="{ 'text-danger': isTooLong }"
                    >
                      {{ textLength }}/{{ maxLength }} znaków
                    </div>
                    <div class="has-error">
                      <span
                        v-for="(error, index) in errors"
                        :key="index"
                        class="help-block"
                      >
                        {{ error }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeNoteModal" class="btn btn-white">Anuluj</button>
          <button
            @click="saveNote"
            :disabled="saving || isEmpty || isTooLong"
            class="btn btn-primary"
          >
            Zapisz
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="editedNote" @click="closeNoteModal" class="modal-backdrop"></div>
</template>

<script>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { hasPermission } from "../../permissions";
import { discountNotePermissionsConst } from "../../constants/productConstants";

// Mirror DiscountNote.MAX_NOTES_PER_DISCOUNT and TEXT_MAX_LENGTH of the API -
// one note per discount for now (the list is ready for more), and only the
// visible text counts to the limit, not the HTML of the formatting.
const MAX_NOTES_PER_DISCOUNT = 1;
const NOTE_MAX_LENGTH = 30;

const SAVE_SUCCESS = "Notatka została zapisana.";
const DELETE_SUCCESS = "Notatka została usunięta.";
const SAVE_ERROR = "Nie udało się zapisać notatki.";
const DELETE_ERROR = "Nie udało się usunąć notatki.";

// The same text the API counts - the text content of the HTML.
const plainText = (html) =>
  new DOMParser().parseFromString(html || "", "text/html").body.textContent ||
  "";

const asList = (value) => {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
};

// Notes of the discount selected in the tree, shown like the notes of an
// order. The saved list is emitted back, so the node (and its label in the
// tree) shows it without reloading the tree.
export default {
  name: "DiscountNotes",
  props: {
    discount: { type: Object, default: null },
  },
  emits: ["change"],
  setup(props, { emit }) {
    const store = useStore();
    // Notes have their own permissions, apart from the ones of discounts.
    const canAdd = computed(() =>
      hasPermission(discountNotePermissionsConst.add)
    );
    const canChange = computed(() =>
      hasPermission(discountNotePermissionsConst.update)
    );
    const canDelete = computed(() =>
      hasPermission(discountNotePermissionsConst.delete)
    );

    const editedNote = ref(null);
    const noteToDelete = ref(null);
    const saving = ref(false);
    const errors = ref([]);

    const notes = computed(() => props.discount?.notes || []);
    const textLength = computed(() => plainText(editedNote.value?.text).length);
    const isEmpty = computed(() => !plainText(editedNote.value?.text).trim());
    const isTooLong = computed(() => textLength.value > NOTE_MAX_LENGTH);

    const isEdited = (note) =>
      !!note.date_modified && note.date_modified !== note.date_added;

    // A copy, so cancelling leaves the saved note untouched.
    const openNoteModal = (note) => {
      errors.value = [];
      editedNote.value = note
        ? { pk: note.pk, text: note.text }
        : { pk: null, text: "" };
    };

    const closeNoteModal = () => {
      errors.value = [];
      editedNote.value = null;
    };

    const showToaster = (type, content) => {
      store.dispatch("showToaster", { type, content });
    };

    const saveNote = () => {
      if (!props.discount || !editedNote.value || saving.value) {
        return;
      }
      const pk = editedNote.value.pk;
      if (!(pk ? canChange.value : canAdd.value)) {
        return;
      }
      errors.value = [];
      saving.value = true;
      store
        .dispatch("saveDiscountNote", {
          pk,
          discount: props.discount.pk,
          text: editedNote.value.text,
        })
        .then((response) => {
          if (response?.status === 200 || response?.status === 201) {
            emit(
              "change",
              pk
                ? notes.value.map((note) =>
                    note.pk === pk ? response.data : note
                  )
                : notes.value.concat([response.data])
            );
            editedNote.value = null;
            showToaster("success", SAVE_SUCCESS);
            return;
          }
          const data = response?.data;
          errors.value = asList(data?.text)
            .concat(asList(data?.non_field_errors))
            .concat(asList(data?.discount))
            .concat(asList(data?.detail));
          if (!errors.value.length) {
            showToaster("error", SAVE_ERROR);
          }
        })
        .catch(() => showToaster("error", SAVE_ERROR))
        .finally(() => {
          saving.value = false;
        });
    };

    const deleteNote = () => {
      const note = noteToDelete.value;
      if (!canDelete.value || !note || saving.value) {
        return;
      }
      saving.value = true;
      store
        .dispatch("deleteDiscountNote", { pk: note.pk })
        .then((response) => {
          // A note already removed elsewhere is gone as well.
          if (response?.status === 204 || response?.status === 404) {
            emit(
              "change",
              notes.value.filter((item) => item.pk !== note.pk)
            );
            showToaster("success", DELETE_SUCCESS);
            return;
          }
          showToaster("error", response?.data?.detail || DELETE_ERROR);
        })
        .catch(() => showToaster("error", DELETE_ERROR))
        .finally(() => {
          noteToDelete.value = null;
          saving.value = false;
        });
    };

    // A note being written belongs to the discount it was started on.
    watch(
      () => props.discount?.pk,
      () => {
        closeNoteModal();
        noteToDelete.value = null;
      }
    );

    return {
      canAdd,
      canChange,
      canDelete,
      notes,
      maxNotes: MAX_NOTES_PER_DISCOUNT,
      maxLength: NOTE_MAX_LENGTH,
      // Only inline formatting - the API drops anything else.
      toolbar: [["bold", "italic", "underline", "strike"], ["clean"]],
      editedNote,
      noteToDelete,
      saving,
      errors,
      textLength,
      isEmpty,
      isTooLong,
      isEdited,
      openNoteModal,
      closeNoteModal,
      saveNote,
      deleteNote,
    };
  },
};
</script>

<style scoped>
.discount-notes-subject {
  margin-left: 6px;
}

.discount-note-text :deep(p) {
  margin: 0;
}

.discount-note-counter {
  font-size: 11px;
  text-align: right;
  margin-top: 4px;
}

/* Bootstrap 5 has no .btn-xs - the same size as the buttons of order notes. */
.btn-xs {
  padding: 1px 5px;
  font-size: 12px;
  line-height: 1.5;
  border-radius: 3px;
}
</style>
